// UserAPI.js
import { db, storage } from "../firebase";
import {
  collection,
  query,
  where,
  orderBy,
  limit,
  getDoc,
  doc,
  updateDoc,
  setDoc,
  deleteField,
} from "firebase/firestore";
import { getDocs } from "firebase/firestore";
import { ref, uploadBytes, getDownloadURL } from "firebase/storage";
import { isMemberConsentField } from "../utils/userFields";

class UserAPI {
  static collectionName = "users"; // Static property for the collection name

  static async fetchUsersByIDs(values) {
    return UserAPI.fetchDocumentsByPropertyValues("userID", values);
  }

  static async getUserById(userID) {
    const userDoc = await getDoc(doc(db, UserAPI.collectionName, userID));
    return userDoc.exists() ? userDoc.data() : null;
  }

  // The caller's own userLocations/{uid} doc (owner-readable per rules), or null.
  static async getUserLocation(uid) {
    const snap = await getDoc(doc(db, "userLocations", uid));
    return snap.exists() ? snap.data() : null;
  }

  static async getUserByPartialName(name, limitCount = 5) {
    const usersCollection = collection(db, UserAPI.collectionName);
    const capitalizedName = this.capitalizeName(name);

    const q = query(
      usersCollection,
      where("name", ">=", capitalizedName),
      where("name", "<=", capitalizedName + "\uf8ff"),
      orderBy("name"),
      limit(limitCount)
    );
    const querySnapshot = await getDocs(q);

    const users = [];
    querySnapshot.forEach((doc) => {
      users.push({ id: doc.id, ...doc.data() });
    });

    return users;
  }

  static capitalizeName(name) {
    return name.charAt(0).toUpperCase() + name.slice(1).toLowerCase();
  }

  static async createUser(fbUser) {
    const userDoc = doc(db, UserAPI.collectionName, fbUser.uid);
    const userSnapshot = await getDoc(userDoc);
    if (userSnapshot.exists()) {
      return userSnapshot.data();
    }

    const user = {
      userID: fbUser.uid,
      name: fbUser.displayName || "",
      photoURL: fbUser.photoURL || "",
      ...(fbUser.phoneNumber ? { phone: fbUser.phoneNumber } : {}),
      ...(fbUser.email ? { email: fbUser.email } : {}),
    };

    await setDoc(userDoc, user);
    return user;
  }

  static async uploadUserPhoto(userID, photoFile) {
    try {
      const photoRef = ref(storage, `profile/${userID}/photo.jpg`);
      await uploadBytes(photoRef, photoFile, {
        cacheControl: "public, max-age=86400",
        contentType: photoFile.type,
      });
      const downloadURL = await getDownloadURL(photoRef);
      return downloadURL;
    } catch (error) {
      console.error("Error uploading user photo: ", error);
      throw error;
    }
  }

  static async setCustomPrice(userID, price) {
    const userRef = doc(db, "users", userID);
    await updateDoc(userRef, { customPrice: price });
  }

  static async setUserField(userID, field, value) {
    if (isMemberConsentField(field)) {
      throw new Error(`"${field}" is a member consent setting; only the member can change it`);
    }
    const userRef = doc(db, "users", userID);
    await updateDoc(userRef, { [field]: value });
  }

  static async removeUserField(userID, field) {
    if (isMemberConsentField(field)) {
      throw new Error(`"${field}" is a member consent setting; only the member can change it`);
    }
    const userRef = doc(db, "users", userID);
    await updateDoc(userRef, { [field]: deleteField() });
  }

  static async updateUserProfile(userID, name, photoURL) {
    try {
      const userRef = doc(db, "users", userID);
      await updateDoc(userRef, {
        name,
        photoURL,
      });

      // Return updated user data
      const updatedUser = {
        userID,
        name,
        photoURL,
      };

      localStorage.setItem("user", JSON.stringify(updatedUser));
      return updatedUser;
    } catch (error) {
      console.error("Error updating user profile: ", error);
      throw error;
    }
  }
}

export default UserAPI;
